import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MAILING_FIELDS } from '@/app/(main)/clients/create/const/passenger';
import { getInheritedUnderageContact } from '@/app/(main)/clients/utils/getInheritedUnderageContact';
import Checkbox from '@/app/components/Checkbox';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import { GENDER_OPTIONS } from '@/consts/options';
import { RelationshipOptions, type RelatedPassengerBasePath } from '@/types/clientsApiTypes';
import type { GenderType } from '@/types/common';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import { RelatedPassengerAdditionalPhonesAndEmails } from '../../../components/form/RelatedPassengerAdditionalPhonesAndEmails';
import { getAge } from '../../utils/getAge';

interface RelatedPassengerMainInfoProps {
  basePath: RelatedPassengerBasePath;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const RelatedPassengerMainInfo = ({ basePath, onUpdateFormData }: RelatedPassengerMainInfoProps) => {
  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [showChildTooltip, setShowChildTooltip] = useState(false);

  const gender = watch(`${basePath}.client.gender`);
  const isSameAsPrimary = watch(`${basePath}.client.same_as_primary`);
  const birthday = watch(`${basePath}.client.date_of_birth`);
  const primaryPassenger = getValues('primary_passenger.client');

  const age = getAge(birthday);
  const isChild = age !== null && age < 18;
  const isCertify = watch(`${basePath}.client.userHasCertifiedTheInformation`);

  const handleCertifyInformationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(`${basePath}.client.userHasCertifiedTheInformation`, event.target.checked, { shouldValidate: true });
    onUpdateFormData?.(`${basePath}.client.userHasCertifiedTheInformation`, event.target.checked);
  };

  const handleRelationshipChange = (option: SelectorOption<string | number> | null) => {
    setValue(`${basePath}.client.relationship`, option?.value ?? null, { shouldValidate: true });
    onUpdateFormData?.(`${basePath}.client.relationship`, option?.value ?? null);
  };

  const handleSetMailingAddress = () => {
    const mailingPayload: Record<string, string> = {};
    MAILING_FIELDS.forEach((field) => {
      setValue(`${basePath}.client.${field}`, primaryPassenger?.[field] ?? '', { shouldValidate: true });
      mailingPayload[field] = primaryPassenger?.[field] ?? '';
    });
    onUpdateFormData?.(basePath, mailingPayload);
  };

  const handleChangeDate = (date: string) => {
    if (!date) {
      setValue(`${basePath}.client.date_of_birth`, '', { shouldDirty: true, shouldValidate: true });
      onUpdateFormData?.(`${basePath}.client.date_of_birth`, null);
      return;
    }

    setValue(`${basePath}.client.date_of_birth`, date, { shouldDirty: true, shouldValidate: true });

    const age = getAge(date);
    if (age === null) return;

    onUpdateFormData?.(`${basePath}.client.date_of_birth`, formatMMDDYYYYToYYYYMMDD(date));
    setValue(`${basePath}.client.same_as_primary`, true, { shouldDirty: true, shouldValidate: true });

    if (!isSameAsPrimary) {
      handleSetMailingAddress();
    }

    if (age < 18) {
      handleSetMailingAddress();
      const inheritedContact = getInheritedUnderageContact(primaryPassenger);
      if (inheritedContact) {
        setValue(`${basePath}.client.email`, inheritedContact.email, { shouldDirty: true, shouldValidate: true });
        setValue(`${basePath}.client.phone`, inheritedContact.phone, { shouldDirty: true, shouldValidate: true });
        onUpdateFormData?.(`${basePath}.client.email`, inheritedContact.email);
        onUpdateFormData?.(`${basePath}.client.phone`, inheritedContact.phone);
      }
    }
  };

  const handleChangeGender = (option: SelectorOption<GenderType> | null) => {
    if (!option) return;
    setValue(`${basePath}.client.gender`, option.value, { shouldDirty: true, shouldValidate: true });
    onUpdateFormData?.(`${basePath}.client.gender`, option.value);
  };

  useEffect(() => {
    if (!isChild) return;

    setShowChildTooltip(true);
    const timer = setTimeout(() => {
      setShowChildTooltip(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isChild]);

  return (
    <div className="grid grid-cols-1 gap-6">
      <Checkbox
        {...register(`${basePath}.client.userHasCertifiedTheInformation`)}
        checked={isCertify}
        size="medium"
        variant="transparent"
        onChange={handleCertifyInformationChange}
        label="I certify the information below matches the information that appears on the passenger’s government-issued ID."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          intent="primaryWhite"
          label="First name"
          placeholder="Enter first name"
          {...register(`${basePath}.client.first_name`)}
          onBlur={(e) => onUpdateFormData?.(e.target.name, e.target.value)}
          errors={errors}
        />
        <Input
          intent="primaryWhite"
          placeholder="Enter middle name"
          label="Middle name"
          {...register(`${basePath}.client.middle_name`)}
          onBlur={(e) => onUpdateFormData?.(e.target.name, e.target.value)}
          errors={errors}
        />
        <Input
          intent="primaryWhite"
          label="Last name"
          placeholder="Enter last name"
          {...register(`${basePath}.client.last_name`)}
          onBlur={(e) => onUpdateFormData?.(e.target.name, e.target.value)}
          errors={errors}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Preferred name"
          intent="primaryWhite"
          placeholder="Enter the name you prefer to be addressed by"
          {...register(`${basePath}.client.preferred_name`)}
          onBlur={(e) => onUpdateFormData?.(e.target.name, e.target.value)}
          errors={errors}
        />

        <Controller
          name={`${basePath}.client.relationship`}
          control={control}
          render={({ field }) => {
            const isLegacy = !!field.value && !RelationshipOptions.some((opt) => opt.value === field.value);
            return (
              <div>
                <Selector
                  options={RelationshipOptions}
                  name={`${basePath}.client.relationship`}
                  option={isLegacy ? null : field.value}
                  value={isLegacy ? '' : (field.value ?? '')}
                  onChange={handleRelationshipChange}
                  label="Relationship to primary passenger"
                  placeholder={isLegacy ? `${field.value} (legacy)` : 'Select relationship'}
                  menuPosition="fixed"
                  errors={errors}
                />
                {isLegacy && (
                  <p className="text-red-500 text-body-small mt-1">
                    Unsupported relationship. Please select a new one.
                  </p>
                )}
              </div>
            );
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <Controller
            name={`${basePath}.client.date_of_birth`}
            control={control}
            render={({ field }) => (
              <SingleDatePicker
                name={`${basePath}.client.date_of_birth`}
                label="Date of birth"
                intent="primaryWhite"
                maxDate={new Date()}
                errors={errors}
                value={field.value || ''}
                onChangeDate={handleChangeDate}
              />
            )}
          />

          {isChild && (
            <p className="mt-1 text-body-small text-red-500">
              This passenger is under 18. Contact details will default to the primary passenger.
            </p>
          )}
        </div>

        <Selector
          className="w-full"
          options={GENDER_OPTIONS}
          value={gender || ''}
          label="Gender"
          onChange={handleChangeGender}
          placeholder="Select gender"
          menuPosition="fixed"
          maxMenuHeight={220}
          errors={errors}
          name={`${basePath}.client.gender`}
        />
      </div>

      <RelatedPassengerAdditionalPhonesAndEmails
        fieldPrefix={`${basePath}.client`}
        deleteButtonsType="large"
        isChild={isChild}
        showChildTooltip={showChildTooltip}
        onUpdateFormData={onUpdateFormData}
      />
    </div>
  );
};
